import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { ClassUnifiedApi } from "../../api/ClassUnifiedApi";
import type { ClassEnrollmentResult } from "../../generated/dto/ClassEnrollmentResult";
import type { ClassInstructorResult } from "../../generated/dto/ClassInstructorResult";
import type { ClassUnifiedCompareClassesRequest } from "../../generated/dto/ClassUnifiedCompareClassesRequest";
import type { ClassUnifiedCreateGuidedSailRequest } from "../../generated/dto/ClassUnifiedCreateGuidedSailRequest";
import type { ClassUnifiedEnrollInstructorRequest } from "../../generated/dto/ClassUnifiedEnrollInstructorRequest";
import type { ClassUnifiedEnrollRequest } from "../../generated/dto/ClassUnifiedEnrollRequest";
import type { ClassUnifiedGuidedSailTimeslotsRequest } from "../../generated/dto/ClassUnifiedGuidedSailTimeslotsRequest";
import type { ClassUnifiedMember } from "../../generated/dto/ClassUnifiedMember";
import type { ClassUnifiedPublic } from "../../generated/dto/ClassUnifiedPublic";
import type { ClassUnifiedSavePersonClassOverrideRequest } from "../../generated/dto/ClassUnifiedSavePersonClassOverrideRequest";
import type { ClassUnifiedSearchClassUnifiedRequest } from "../../generated/dto/ClassUnifiedSearchClassUnifiedRequest";
import type { ClassUnifiedUnenrollInstructorRequest } from "../../generated/dto/ClassUnifiedUnenrollInstructorRequest";
import type { ClassUnifiedUnenrollRequest } from "../../generated/dto/ClassUnifiedUnenrollRequest";
import type { GuidedSailDay } from "../../generated/dto/GuidedSailDay";
import type { PersonClassEnrollment } from "../../generated/dto/PersonClassEnrollment";
import type { PersonClassesPage } from "../../generated/dto/PersonClassesPage";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { UnifiedClassComparisonReport } from "../../generated/dto/UnifiedClassComparisonReport";
import type { UnifiedClassSearchOptions } from "../../generated/dto/UnifiedClassSearchOptions";
import type { UnifiedStaffClassesResponse } from "../../generated/dto/UnifiedStaffClassesResponse";

export class ClassUnifiedApiClient implements ClassUnifiedApi {
  constructor(private readonly client = useClient()) {}

  async classesStaff(options: UnifiedClassSearchOptions): Promise<UnifiedStaffClassesResponse> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/classes`, serializeCbiDto(options, "generated.dto.UnifiedClassSearchOptions"));
    return deserializeCbiDto<UnifiedStaffClassesResponse>(data, "generated.dto.UnifiedStaffClassesResponse");
  }

  async compareClassesStaff(request: ClassUnifiedCompareClassesRequest): Promise<UnifiedClassComparisonReport> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/compareclasses`, serializeCbiDto(request, "generated.dto.ClassUnifiedCompareClassesRequest"));
    return deserializeCbiDto<UnifiedClassComparisonReport>(data, "generated.dto.UnifiedClassComparisonReport");
  }

  async compareClassesPublic(request: ClassUnifiedCompareClassesRequest): Promise<UnifiedClassComparisonReport> {
    const { data } = await this.client.post<unknown>(`/classunified/public/compareclasses`, serializeCbiDto(request, "generated.dto.ClassUnifiedCompareClassesRequest"));
    return deserializeCbiDto<UnifiedClassComparisonReport>(data, "generated.dto.UnifiedClassComparisonReport");
  }

  async createGuidedSailStaff(request: ClassUnifiedCreateGuidedSailRequest): Promise<ClassInstructorResult> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/createguidedsail`, serializeCbiDto(request, "generated.dto.ClassUnifiedCreateGuidedSailRequest"));
    return deserializeCbiDto<ClassInstructorResult>(data, "generated.dto.ClassInstructorResult");
  }

  async createGuidedSailMember(request: ClassUnifiedCreateGuidedSailRequest): Promise<ClassInstructorResult> {
    const { data } = await this.client.post<unknown>(`/classunified/member/createguidedsail`, serializeCbiDto(request, "generated.dto.ClassUnifiedCreateGuidedSailRequest"));
    return deserializeCbiDto<ClassInstructorResult>(data, "generated.dto.ClassInstructorResult");
  }

  async enrollStaff(request: ClassUnifiedEnrollRequest): Promise<ClassEnrollmentResult> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/enroll`, serializeCbiDto(request, "generated.dto.ClassUnifiedEnrollRequest"));
    return deserializeCbiDto<ClassEnrollmentResult>(data, "generated.dto.ClassEnrollmentResult");
  }

  async enrollMember(request: ClassUnifiedEnrollRequest): Promise<ClassEnrollmentResult> {
    const { data } = await this.client.post<unknown>(`/classunified/member/enroll`, serializeCbiDto(request, "generated.dto.ClassUnifiedEnrollRequest"));
    return deserializeCbiDto<ClassEnrollmentResult>(data, "generated.dto.ClassEnrollmentResult");
  }

  async enrollInstructorStaff(request: ClassUnifiedEnrollInstructorRequest): Promise<ClassInstructorResult> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/enrollinstructor`, serializeCbiDto(request, "generated.dto.ClassUnifiedEnrollInstructorRequest"));
    return deserializeCbiDto<ClassInstructorResult>(data, "generated.dto.ClassInstructorResult");
  }

  async enrollInstructorMember(request: ClassUnifiedEnrollInstructorRequest): Promise<ClassInstructorResult> {
    const { data } = await this.client.post<unknown>(`/classunified/member/enrollinstructor`, serializeCbiDto(request, "generated.dto.ClassUnifiedEnrollInstructorRequest"));
    return deserializeCbiDto<ClassInstructorResult>(data, "generated.dto.ClassInstructorResult");
  }

  async guidedSailTimeslotsStaff(request: ClassUnifiedGuidedSailTimeslotsRequest): Promise<Array<GuidedSailDay>> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/guidedsailtimeslots`, serializeCbiDto(request, "generated.dto.ClassUnifiedGuidedSailTimeslotsRequest"));
    return deserializeCbiDto<Array<GuidedSailDay>>(data, "generated.dto.GuidedSailDay");
  }

  async guidedSailTimeslotsMember(request: ClassUnifiedGuidedSailTimeslotsRequest): Promise<Array<GuidedSailDay>> {
    const { data } = await this.client.post<unknown>(`/classunified/member/guidedsailtimeslots`, serializeCbiDto(request, "generated.dto.ClassUnifiedGuidedSailTimeslotsRequest"));
    return deserializeCbiDto<Array<GuidedSailDay>>(data, "generated.dto.GuidedSailDay");
  }

  async memberClassesMember(options: UnifiedClassSearchOptions): Promise<Array<ClassUnifiedMember>> {
    const { data } = await this.client.post<unknown>(`/classunified/member/memberclasses`, serializeCbiDto(options, "generated.dto.UnifiedClassSearchOptions"));
    return deserializeCbiDto<Array<ClassUnifiedMember>>(data, "generated.dto.ClassUnifiedMember");
  }

  async personEnrollmentsStaff(person: PersonRef): Promise<Array<PersonClassEnrollment>> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/personenrollments`, serializeCbiDto(person, "generated.dto.PersonRef"));
    return deserializeCbiDto<Array<PersonClassEnrollment>>(data, "generated.dto.PersonClassEnrollment");
  }

  async personEnrollmentsMember(person: PersonRef): Promise<Array<PersonClassEnrollment>> {
    const { data } = await this.client.post<unknown>(`/classunified/member/personenrollments`, serializeCbiDto(person, "generated.dto.PersonRef"));
    return deserializeCbiDto<Array<PersonClassEnrollment>>(data, "generated.dto.PersonClassEnrollment");
  }

  async publicClassesPublic(options: UnifiedClassSearchOptions): Promise<Array<ClassUnifiedPublic>> {
    const { data } = await this.client.post<unknown>(`/classunified/public/publicclasses`, serializeCbiDto(options, "generated.dto.UnifiedClassSearchOptions"));
    return deserializeCbiDto<Array<ClassUnifiedPublic>>(data, "generated.dto.ClassUnifiedPublic");
  }

  async savePersonClassOverrideStaff(request: ClassUnifiedSavePersonClassOverrideRequest): Promise<void> {
    await this.client.post(`/classunified/staff/savepersonclassoverride`, serializeCbiDto(request, "generated.dto.ClassUnifiedSavePersonClassOverrideRequest"));
  }

  async searchClassUnifiedStaff(request: ClassUnifiedSearchClassUnifiedRequest): Promise<PersonClassesPage> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/searchclassunified`, serializeCbiDto(request, "generated.dto.ClassUnifiedSearchClassUnifiedRequest"));
    return deserializeCbiDto<PersonClassesPage>(data, "generated.dto.PersonClassesPage");
  }

  async unenrollStaff(request: ClassUnifiedUnenrollRequest): Promise<ClassEnrollmentResult> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/unenroll`, serializeCbiDto(request, "generated.dto.ClassUnifiedUnenrollRequest"));
    return deserializeCbiDto<ClassEnrollmentResult>(data, "generated.dto.ClassEnrollmentResult");
  }

  async unenrollMember(request: ClassUnifiedUnenrollRequest): Promise<ClassEnrollmentResult> {
    const { data } = await this.client.post<unknown>(`/classunified/member/unenroll`, serializeCbiDto(request, "generated.dto.ClassUnifiedUnenrollRequest"));
    return deserializeCbiDto<ClassEnrollmentResult>(data, "generated.dto.ClassEnrollmentResult");
  }

  async unenrollInstructorStaff(request: ClassUnifiedUnenrollInstructorRequest): Promise<ClassInstructorResult> {
    const { data } = await this.client.post<unknown>(`/classunified/staff/unenrollinstructor`, serializeCbiDto(request, "generated.dto.ClassUnifiedUnenrollInstructorRequest"));
    return deserializeCbiDto<ClassInstructorResult>(data, "generated.dto.ClassInstructorResult");
  }

  async unenrollInstructorMember(request: ClassUnifiedUnenrollInstructorRequest): Promise<ClassInstructorResult> {
    const { data } = await this.client.post<unknown>(`/classunified/member/unenrollinstructor`, serializeCbiDto(request, "generated.dto.ClassUnifiedUnenrollInstructorRequest"));
    return deserializeCbiDto<ClassInstructorResult>(data, "generated.dto.ClassInstructorResult");
  }
}

export const useClassUnifiedApi = (): ClassUnifiedApi => new ClassUnifiedApiClient();
